import React, {forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import _ from 'lodash';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {
  activeContext,
  getEdReviewSessionDetails,
  KIND_OF_REVIEW_STATE,
  openReviewModal,
  pushToTemporaryEventsList,
  reviewListSelector,
  selectedDate,
  setSelectedPatient,
  setSlotManipulationState,
  slotManipulationState,
} from '../../../../../../../../store/reducers/reviews';
import {Grid, Paper} from '@mui/material';
import {
  ED_REVIEW_MODALS,
  getGroupedEvents,
  getLockedSlots,
  getReviewsScheduledOnGivenDate,
  getVisibleWidth,
  SLOT_MANIPULATION_OPTIONS,
} from '../../../../utils/edReviewUtils';
import TimeSlots, {TimeColumn} from './calendarTiles/timeSlots';
import CurrentTimeHorizontalLine from './calendarTiles/timeHorizontalLine';
import DailyCalendarColumns from './dailyCalendarColumns';
import {setSnackbar} from '../../../../../../../../store/reducers/globals';
import {
  canCreateNewLockedSession,
  canCreateNewSingleSession,
  createNewLockedSession,
  createNewSingleSession,
  getManipulationKind,
  isManipulatingSlots,
} from '../../utils/sessionUtilities';
import ColumnHeader from './calendarTiles/columnHeader';

export const useDailyCalendarStyles = makeStyles((theme) => ({
  timeContainer: {
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 405,
    overflow: 'hidden',
    position: 'sticky',
    flexBasis: DEFAULT_LEFT,
    borderRight: `1px solid ${theme.palette.background.paperHover}`,
  },
  timelineGrid: {
    // overflow: 'hidden',
    flexWrap: 'nowrap',
    position: "relative",
    borderRight: `1px solid${theme.palette.background.paperHover}`,
  },
  timelineContainer: {
    flex: '1 0 0',
    padding: '0 6px',
    borderLeft: 'none',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'space-around',
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.paperHover} 1px, transparent 1px)`,
  },
  timelineSlot: {
    width: '100%',
    position:'absolute',

    "&.borderBottom": {
      height: 1,
      borderBottom: `1px solid ${theme.palette.background.paperHover}`,
    }
  },
  time: {
    width: "70px",
    fontWeight: "bold",
    textAlign: "center",
    verticalAlign: "middle",
    backgroundColor: theme.palette.background.default,

    "&.odd": {
      color: "grey",
    },
  },
  event: {
    flex: "1",
    position: "absolute",
    backgroundColor: "lightblue",
  },
  currentTimeLine: {
    height: 2,
    position: "absolute",
    backgroundColor: theme.palette.primary.main,
  },
  currentTimeLineContainer: {
    height: 30,
    color: "#FFF",
    padding: "4px",
    paddingLeft: "5px",
    position: "absolute",
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    backgroundColor: theme.palette.primary.main,
  },
  paper: {
    flexGrow: 1,
    borderRadius: 10,
    position: "relative",
    background: `repeating-linear-gradient(-60deg, ${theme.palette.background.paperDarker}, ${theme.palette.background.paperDarker} 20px, ${theme.palette.background.default} 20px, ${theme.palette.background.default} 40px)`,
  },
  columnHeader: {
    top: 50,
    display: "flex",
    zIndex: 1000,
    minHeight: 50,
    paddingTop: 5,
    paddingLeft: 76,
    position: "sticky",
    alignItems: "center",
    minWidth: "100%",
    width: "fit-content",
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.background.paper}`,

    "&::before": {
      left: 0,
      top: 60,
      height: 1,
      width: 70,
      content: '""',
      display: "block",
      position: "absolute",
      backgroundColor: theme.palette.background.default,
    },
  },
  columnClinicianHeader: {
    minHeight: 50,
    display: "flex",
    borderRadius: 7,
    marginBottom: 10,
    padding: "0 10px",
    alignItems: "center",
    backgroundColor: theme.palette.background.paperDarker,
  },
  specialty: {
    marginRight: 10,
    borderRadius: 7,
    padding: "3px 5px",
    border: `1px solid ${theme.palette.mode === "dark" ? theme.palette.background.paperHover : theme.palette.background.paper}`,
  },
  dropZone: {
    "&.is-over": {
      backgroundColor: "rgba(0, 0, 255, .2)",
    },
  },
  dropZoneIcon: {
    "&.can-drop": {
      background: "grey !important",
    },
    "&.is-over": {
      // opacity: 0.7,
    },
  },
}));

export const SLOT_HEIGHT = 150;
export const DEFAULT_LEFT = 70;
export const LEFT_OFFSET = 2;
export const OFFSET = 2;
export const COLUMN_GAP = 7;

const MIN_CONTAINER_WIDTH = 300;

const EdReviewCalendarDaily = forwardRef(
  ({ date, handleSelect, events, dailySlots, onSelectDate }, ref) => {
    const dispatch = useDispatch();
    const classes = useDailyCalendarStyles();

    const edReviews = useSelector(reviewListSelector);
    const calendarDate = useSelector(selectedDate);
    const manipulationState = useSelector(slotManipulationState);
    const kindOfActiveEvents = useSelector(activeContext);

    // Events
    const [originalSessionColumns, setOriginalSessionColumns] = useState([]);
    const [temporaryEditedSessions, setTemporaryEditedSessions] = useState([]);

    // Current timeline
    const [currentTime, setCurrentTime] = useState(moment());
    const currenLine = useRef(null);
    const refTimeline = useRef(null);

    const timelineVisibleWidth =
      getVisibleWidth(refTimeline.current) - (DEFAULT_LEFT + 20) || 0;
    const OFFSET_LEFT = ref.current?.getBoundingClientRect().width || 0;
    const selectedDay = moment(date, "DD/MM/YYYY");

    // LIST STATE (Reviews, locked slots, etc.)
    const reviewsToday = getReviewsScheduledOnGivenDate(_.cloneDeep(edReviews), selectedDay);
    const lockedSlots = getLockedSlots(events, true);

    useEffect(() => {
      if (!_.isEmpty(events)) {
        const clone = _.cloneDeep(events);
        const columns = getGroupedEvents(clone, calendarDate);

        setOriginalSessionColumns(columns);
        setTemporaryEditedSessions(columns);
        dispatch(setSlotManipulationState({ dayCalendarColumns: columns }));
      }

      return () => {
        setOriginalSessionColumns([]);
        setTemporaryEditedSessions([]);
        dispatch(setSlotManipulationState({ dayCalendarColumns: [] }));

      };
    }, [events]);

    // Update current time line
    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentTime(moment());
      }, 60000);

      if (currenLine.current)
        currenLine.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

      return () => {
        clearInterval(interval);
      };
    }, [currenLine.current]);

    const handleSelectDateType = (time, temporaryLock, noHandleSelect) => {
      let selectedTime = time;
      selectedTime.seconds(0);
      selectedTime.milliseconds(0);
      if (!noHandleSelect) {
        if (temporaryLock) {
          const time = moment(selectedTime);
          handleSelect(time);
          return;
        }
        handleSelect(selectedTime);
      } else {
        return selectedTime;
      }
    };

    const calculateTimeForTop = (top) => {
      const minutesFromStartOfDay = (top / SLOT_HEIGHT) * 30;
      const startOfDay = moment(selectedDay).startOf("day");
      return startOfDay.add(minutesFromStartOfDay, "minutes");
    };

    const handleSelectTime = (e, noSelect = false, options, columnIndex) => {
      if (!e) {
        return handleSelect(null);
      }

      const timeline = document.getElementById("timeline-calendar");
      const rect = timeline.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const clientY = e.clientY + scrollY;
      const posY = clientY - rect.top - scrollY;

      if (handleSelect) {
        return handleSelectDateType(
          calculateTimeForTop(posY),
          false,
          false
        )
      }

      if (isManipulatingSlots(manipulationState)) {
        const selectedTime = calculateTimeForTop(posY);
        const manipulationKind = getManipulationKind(manipulationState);

        if (manipulationKind === SLOT_MANIPULATION_OPTIONS.ASSIGN) {
          const canAssignSession = canCreateNewLockedSession(moment(options?.startDate), options, manipulationState?.temporaryEvents, reviewsToday);

          if (canAssignSession?.type === 'error') {
            return dispatch(setSnackbar({ type: canAssignSession?.type, message: canAssignSession?.message || `Cannot assign slot at ${selectedTime.format("HH:mm a")}` }));
          }

          const parentSession = dispatch(getEdReviewSessionDetails({
            kindOfActiveEvents,
            sessionId: options?.sessionId,
            type: 'regular'
          }));

          if (parentSession?.sessionId && (kindOfActiveEvents === KIND_OF_REVIEW_STATE.BARS ? parentSession?.rrule : true)) {
            return dispatch(openReviewModal(ED_REVIEW_MODALS.ASSIGN_SESSION, {session: parentSession, hour: selectedTime.format("LT")}))
          } else {
            return dispatch(setSnackbar({ type: 'error', message: `Could not find valid event. Please try again later or contact support` }));
          }
        } else if (manipulationKind === SLOT_MANIPULATION_OPTIONS.LOCK_SLOT) {
          const canLockSession = canCreateNewLockedSession(selectedTime, options, manipulationState?.temporaryEvents, reviewsToday);

          if (canLockSession?.type === 'error') {
            return dispatch(setSnackbar({ type: canLockSession?.type, message: canLockSession?.message || `Cannot lock slot at ${selectedTime.format("HH:mm a")}` }));
          }

          const newLockedSlot = createNewLockedSession(selectedTime, options);
          return dispatch(pushToTemporaryEventsList(newLockedSlot?.parentSession, newLockedSlot));
        } else if (manipulationKind === SLOT_MANIPULATION_OPTIONS.CREATE_SLOT) {
          const canCreateSession = canCreateNewSingleSession(selectedTime, options, manipulationState?.temporaryEvents, columnIndex);

          if (canCreateSession?.type === 'error') {
            return dispatch(setSnackbar({ type: canCreateSession?.type, message: canCreateSession?.message || `Cannot lock slot at ${selectedTime.format("HH:mm a")}` }));
          }

          const newSlot = createNewSingleSession(selectedTime, options);
          return dispatch(pushToTemporaryEventsList(newSlot.parentSession, newSlot, columnIndex));
        } else {
          console.log("NOTHING WILL HAPPEN");
          return null;
        }
      } else {
        if (noSelect) {
          return handleSelectDateType(calculateTimeForTop(posY), false, noSelect);
        } else {
          handleSelectDateType(calculateTimeForTop(posY), false, noSelect);
        }
      }
    };

    const calculateTopForCurrentTime = (time) => {
      const startOfDay = moment(currentTime).startOf("day");
      const minutesFromStartOfDay = currentTime.diff(startOfDay, "minutes");
      const top = (minutesFromStartOfDay / 30) * SLOT_HEIGHT - (time ? 15 : 0);
      return `${top}px`;
    };

    const getColumnWidth = (cols) => {
      let columns = cols;
      if (!_.isArray(cols)) {
        columns = [];
      }

      const width = timelineVisibleWidth / (columns.length || 1);

      return width > MIN_CONTAINER_WIDTH ? width : MIN_CONTAINER_WIDTH;
    }

    const selectPatient = (ev, pt) => {
      dispatch(setSelectedPatient({ patient: pt, startDate: handleSelectTime(ev) }));
      handleSelectTime(ev);
    };

    const getEventIndex = useCallback((sessionId) => {
      return temporaryEditedSessions.findIndex((col) => {
        return col?.events?.find(
          (event) => event?.sessionId === sessionId || event?.id === sessionId,
        );
      });
    }, [temporaryEditedSessions]);

    const columnWidth = useMemo(() => getColumnWidth(temporaryEditedSessions), [temporaryEditedSessions]);

    return (
      <Paper className={clsx(classes.paper)}>
          <ColumnHeader
            // ref={refColumns}
            columns={temporaryEditedSessions}
            columnHeaderStyles={{
              minWidth: MIN_CONTAINER_WIDTH,
              width: getColumnWidth(temporaryEditedSessions + (5 * LEFT_OFFSET)) / temporaryEditedSessions?.length,
            }}
          />

        <div id="timeline-calendar">
          <Grid
            container
            ref={refTimeline}
            className={classes.timelineGrid}
            style={{
              flexBasis: `calc(100% - ${2 * LEFT_OFFSET})`,
            }}
          >
            <Grid
              item
              className={classes.timeContainer}
            >
              <TimeColumn />

              <CurrentTimeHorizontalLine
                date={date}
                ref={currenLine}
                onlyTimeIndicator
                leftOffset={OFFSET_LEFT}
                yAxisFromTop={calculateTopForCurrentTime}
                width={refTimeline.current?.getBoundingClientRect()?.width}
              />
            </Grid>

            <Grid
              item
              container
              className={clsx(classes.timelineContainer, 'scroll-normal-white')}
              style={{
                minWidth: `calc(100% - ${2 * DEFAULT_LEFT})`,
                width: LEFT_OFFSET + temporaryEditedSessions.length * ((getColumnWidth(temporaryEditedSessions) + (5 * LEFT_OFFSET))),
              }}
            >
              <TimeSlots width='100%' />

              <CurrentTimeHorizontalLine
                onlyLine
                date={date}
                ref={currenLine}
                leftOffset={OFFSET_LEFT}
                yAxisFromTop={calculateTopForCurrentTime}
                width={refTimeline.current?.getBoundingClientRect()?.width}
              />

              {!_.isEmpty(temporaryEditedSessions) && (
                <DailyCalendarColumns
                  date={date}
                  events={events}
                  dailySlots={dailySlots}
                  lockedSlots={lockedSlots}
                  OFFSET_LEFT={OFFSET_LEFT}
                  columnWidth={columnWidth}
                  reviewsToday={reviewsToday}
                  getEventIndex={getEventIndex}
                  selectPatient={selectPatient}
                  onSelectTime={handleSelectTime}
                  temporaryEditedSessions={temporaryEditedSessions}
                />
              )}
            </Grid>
          </Grid>
        </div>
      </Paper>
    );
  },
);

EdReviewCalendarDaily.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dailySlots: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  handleSelect: PropTypes.func,
  events: PropTypes.array,
};

export default memo(EdReviewCalendarDaily, _.isEqual);